// src/pages/settings/MutedTopicsSection.tsx
import { useEffect, useRef, useState } from 'react';
import { Alert, Box, Button, Card, Chip, Typography } from '@mui/material';
import { useMutedTopics } from '../markets/board/state/useMutedTopics';
import { useSchema } from '../markets/board/api/useSchema';
import { useAccessFeatures } from '../../auth/useAccessFeatures';
import { canUseFeature } from '../../auth/canUseFeature';
import type { MutedTopics } from '../markets/board/state/mutedTopics';

function toggle(list: string[], value: string): string[] {
    return list.includes(value) ? list.filter(v => v !== value) : [...list, value];
}

function TopicRow({
    options,
    hidden,
    onToggle,
}: {
    options: string[];
    hidden: string[];
    onToggle: (value: string) => void;
}) {
    return (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mt: 1 }}>
            {options.map(opt => {
                const off = hidden.includes(opt);
                return (
                    <Chip
                        key={opt}
                        label={opt}
                        size="small"
                        onClick={() => onToggle(opt)}
                        sx={{
                            height: 26,
                            fontSize: '12px',
                            borderRadius: '6px',
                            border: '1px solid',
                            borderColor: off ? 'divider' : 'transparent',
                            bgcolor: off ? 'transparent' : 'action.hover',
                            color: off ? 'text.disabled' : 'text.primary',
                            textDecoration: off ? 'line-through' : 'none',
                        }}
                    />
                );
            })}
        </Box>
    );
}

export function MutedTopicsSection() {
    const access = useAccessFeatures();
    const { muted, save } = useMutedTopics();
    const { data: schema } = useSchema();
    const [draft, setDraft] = useState<MutedTopics>(muted);
    const [saving, setSaving] = useState(false);
    const [failed, setFailed] = useState(false);

    // Keyed on the stored value, not its identity: a hook that returns a fresh object each render would otherwise reset the draft forever.
    const storedKey = JSON.stringify(muted);
    const storedRef = useRef(muted);
    storedRef.current = muted;

    // The stored value is the source of truth; a reload replaces an untouched draft.
    useEffect(() => {
        setDraft(storedRef.current);
    }, [storedKey]);

    const dirty = JSON.stringify(draft) !== storedKey;

    if (!canUseFeature(access, 'events')) return null;

    return (
        <Card sx={{ p: 3, mt: 3 }}>
            <Typography variant="h6">Hidden topics</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                Hidden topics never appear on the markets board or in search. Struck through means
                hidden.
            </Typography>

            <Typography variant="subtitle2" sx={{ mt: 2.5 }}>
                Tag
            </Typography>
            <TopicRow
                options={schema?.polymarket_tags ?? []}
                hidden={draft.tags}
                onToggle={v => setDraft(d => ({ ...d, tags: toggle(d.tags, v) }))}
            />

            <Typography variant="subtitle2" sx={{ mt: 2.5 }}>
                Event category
            </Typography>
            <TopicRow
                options={schema?.event_categories ?? []}
                hidden={draft.categories}
                onToggle={v => setDraft(d => ({ ...d, categories: toggle(d.categories, v) }))}
            />

            <Box sx={{ mt: 3 }}>
                <Button
                    variant="contained"
                    aria-label="Save hidden topics"
                    disabled={!dirty || saving}
                    onClick={async () => {
                        setSaving(true);
                        setFailed(false);
                        try {
                            await save(draft);
                        } catch {
                            // The draft stays put so the user can retry without retyping.
                            setFailed(true);
                        } finally {
                            setSaving(false);
                        }
                    }}
                >
                    Save hidden topics
                </Button>
                {failed && (
                    <Alert severity="error" sx={{ mt: 2 }}>
                        Your hidden topics could not be saved. Check your connection and try again.
                    </Alert>
                )}
            </Box>
        </Card>
    );
}
